// Service-specific field configuration for the booking form.
// Each service may either define `groups` (multiple sections) or a flat `fields` array.
// Field shape: { name, label, type, required, options?, min?, max?, placeholder?, span? }
// type: text | email | tel | number | date | time | textarea | select | yesno

export const SERVICE_TYPES = [
  "Airport Transfers",
  "Corporate Travel",
  "Hourly Hire",
  "Cruise Transfers",
  "Wedding Transfers",
  "Special Events",
];

export const VEHICLE_OPTIONS = [
  "Executive Sedan",
  "First Class Sedan",
  "Luxury SUV",
  "Executive Van",
  "Luxury Van",
];

// Display name -> pricing engine vehicle key
export const VEHICLE_KEY_BY_NAME = {
  "Executive Sedan": "executive_sedan",
  "First Class Sedan": "first_class_sedan",
  "Luxury SUV": "luxury_suv",
  "Executive Van": "executive_van",
  "Luxury Van": "luxury_van",
};

const PASSENGERS_FIELD = {
  name: "passengers",
  label: "Number of Passengers",
  type: "number",
  min: 1,
  max: 20,
  required: true,
};

const LUGGAGE_FIELD = {
  name: "luggage_count",
  label: "Number of Luggage Items",
  type: "number",
  min: 0,
  max: 40,
  required: true,
};

// Auto-detected airline badge (shown when detectFlight() resolves the carrier)
const AIRLINE_AUTO_FIELD = {
  name: "airline",
  label: "Airline",
  type: "auto_badge",
  showIf: (d) => !!d.airline_auto,
};

// Manual airline fallback — only visible when auto-detection failed
const AIRLINE_MANUAL_FIELD = {
  name: "airline",
  label: "Airline",
  type: "text",
  required: true,
  placeholder: "e.g. Qantas",
  showIf: (d) => !d.airline_auto,
};

const FLIGHT_TYPE_FIELDS = [
  // Read-only badge shown when we auto-detected the flight type from flight number
  {
    name: "flight_type",
    label: "Flight Type",
    type: "auto_badge",
    showIf: (d) => !!d.flight_type_auto,
  },
  // Fallback manual dropdown - only shown when auto-detection was not possible
  {
    name: "flight_type",
    label: "International or Domestic Flight?",
    type: "select",
    options: ["International", "Domestic"],
    required: true,
    showIf: (d) => !d.flight_type_auto,
  },
];

// Airport-specific passenger hierarchy.
// - Total Passengers is always visible.
// - Number of Adults appears when Total Passengers > 1.
// - Number of Children is auto-calculated (Passengers - Adults) and read-only.
// - Child-seat fields only appear when there is at least one child.
const AIRPORT_PASSENGER_FIELDS = [
  {
    name: "passengers",
    label: "Total Passengers",
    type: "number",
    min: 1,
    max: 20,
    required: true,
  },
  {
    name: "adults",
    label: "Number of Adults",
    type: "number",
    min: 1,
    max: 20,
    required: true,
    showIf: (d) => Number(d.passengers) > 1,
  },
  {
    name: "children",
    label: "Number of Children",
    type: "computed",
    showIf: (d) =>
      Number(d.passengers) > 1 &&
      d.adults !== undefined &&
      d.adults !== "" &&
      Number(d.adults) >= 1,
  },
  {
    name: "children_under_6",
    label: "Number of Children Requiring Child Seats",
    type: "number",
    min: 0,
    max: 10,
    required: true,
    showIf: (d) => Number(d.children) > 0,
    helper:
      "Children under 6 years old require appropriate child restraints. Please provide ages so we can prepare the correct child seats for your journey.",
  },
  {
    name: "ages_under_6",
    label: "Age(s) of Children Requiring Child Seats",
    type: "text",
    required: true,
    span: 2,
    placeholder: "e.g. 2, 4, 5",
    showIf: (d) =>
      Number(d.children) > 0 && Number(d.children_under_6) > 0,
  },
];

const OVERSIZED_LUGGAGE_FIELD = {
  name: "oversized_luggage",
  label: "Oversized Luggage? (Golf Clubs, Surfboards, Prams, etc.)",
  type: "yesno",
  required: true,
};

// Airport - Arrival
const AIRPORT_ARRIVAL = [
  { name: "flight_number", label: "Flight Number", type: "text", required: true, placeholder: "e.g. QF7", helper: "We'll detect your airline and flight type automatically." },
  AIRLINE_AUTO_FIELD,
  AIRLINE_MANUAL_FIELD,
  { name: "arrival_airport", label: "Arrival Airport", type: "address", required: true, placeholder: "Sydney Airport (SYD)" },
  { name: "arrival_date", label: "Arrival Date", type: "date", required: true },
  { name: "arrival_time", label: "Arrival Time", type: "time", required: true },
  { name: "dropoff_address", label: "Drop-off Address", type: "address", required: true, span: 2, placeholder: "Start typing an address..." },
  ...AIRPORT_PASSENGER_FIELDS,
  LUGGAGE_FIELD,
  ...FLIGHT_TYPE_FIELDS,
  OVERSIZED_LUGGAGE_FIELD,
];

// Airport - Departure
const AIRPORT_DEPARTURE = [
  { name: "pickup_address", label: "Pickup Address", type: "address", required: true, span: 2, placeholder: "Start typing an address..." },
  { name: "destination_airport", label: "Destination Airport", type: "address", required: true, placeholder: "Sydney Airport (SYD)" },
  { name: "flight_number", label: "Flight Number", type: "text", required: true, placeholder: "e.g. QF7", helper: "We'll detect your airline and flight type automatically." },
  AIRLINE_AUTO_FIELD,
  AIRLINE_MANUAL_FIELD,
  { name: "departure_date", label: "Flight Departure Date", type: "date", required: true },
  { name: "departure_time", label: "Flight Departure Time", type: "time", required: true },
  ...AIRPORT_PASSENGER_FIELDS,
  LUGGAGE_FIELD,
  ...FLIGHT_TYPE_FIELDS,
  OVERSIZED_LUGGAGE_FIELD,
];

// Airport - Return (Both)
const AIRPORT_RETURN_GROUPS = [
  {
    title: "Arrival Details",
    fields: [
      { name: "arrival_flight_number", label: "Flight Number", type: "text", required: true, placeholder: "e.g. QF7", helper: "We'll detect your airline and flight type automatically." },
      { name: "arrival_airline", label: "Airline", type: "auto_badge", showIf: (d) => !!d.arrival_airline_auto },
      { name: "arrival_airline", label: "Airline", type: "text", required: true, placeholder: "e.g. Qantas", showIf: (d) => !d.arrival_airline_auto },
      { name: "arrival_airport", label: "Arrival Airport", type: "address", required: true, placeholder: "Sydney Airport (SYD)" },
      { name: "arrival_date", label: "Arrival Date", type: "date", required: true },
      { name: "arrival_pickup_time", label: "Arrival Pickup Time", type: "time", required: true },
      { name: "arrival_dropoff_address", label: "Drop-off Location", type: "address", required: true, span: 2, placeholder: "Start typing an address..." },
    ],
  },
  {
    title: "Departure Details",
    fields: [
      { name: "return_flight_number", label: "Return Flight Number", type: "text", required: true, placeholder: "e.g. QF1", helper: "We'll detect your airline and flight type automatically." },
      { name: "return_airline", label: "Airline", type: "auto_badge", showIf: (d) => !!d.return_airline_auto },
      { name: "return_airline", label: "Airline", type: "text", required: true, placeholder: "e.g. Qantas", showIf: (d) => !d.return_airline_auto },
      { name: "departure_airport", label: "Departure Airport", type: "address", required: true, placeholder: "Sydney Airport (SYD)" },
      { name: "departure_date", label: "Departure Date", type: "date", required: true },
      { name: "departure_pickup_time", label: "Departure Pickup Time", type: "time", required: true },
    ],
  },
  {
    title: "Additional Details",
    fields: [
      { name: "pickup_address", label: "Pickup Address", type: "address", required: true, span: 2, placeholder: "Start typing an address..." },
      ...AIRPORT_PASSENGER_FIELDS,
      LUGGAGE_FIELD,
      ...FLIGHT_TYPE_FIELDS,
      OVERSIZED_LUGGAGE_FIELD,
    ],
  },
];

// Cruise fields (shared for all transfer types)
const CRUISE_FIELDS = [
  { name: "cruise_ship_name", label: "Cruise Ship Name", type: "text", required: true, placeholder: "e.g. Carnival Splendor" },
  { name: "cruise_terminal", label: "Cruise Terminal", type: "address", required: true, placeholder: "OPT / White Bay / Circular Quay" },
  { name: "cruise_date", label: "Cruise Date", type: "date", required: true },
  { name: "cruise_time", label: "Cruise Time", type: "time", required: true },
  { name: "pickup_date", label: "Pickup Date", type: "date", required: true },
  { name: "pickup_time", label: "Pickup Time", type: "time", required: true },
  { name: "pickup_location", label: "Pickup Location", type: "address", required: true },
  { name: "destination", label: "Destination", type: "address", required: true },
  PASSENGERS_FIELD,
  LUGGAGE_FIELD,
];

export const SERVICE_CONFIG = {
  "Airport Transfers": {
    transferType: {
      name: "transfer_type",
      label: "Transfer Type",
      options: ["Arrival", "Departure", "Return (Both)"],
      required: true,
    },
    byTransfer: {
      Arrival: { fields: AIRPORT_ARRIVAL },
      Departure: { fields: AIRPORT_DEPARTURE },
      "Return (Both)": { groups: AIRPORT_RETURN_GROUPS },
    },
  },
  "Corporate Travel": {
    fields: [
      { name: "company_name", label: "Company Name", type: "text", required: true },
      { name: "pickup_date", label: "Pickup Date", type: "date", required: true },
      { name: "pickup_time", label: "Pickup Time", type: "time", required: true },
      { name: "pickup_location", label: "Pickup Location", type: "address", required: true, span: 2 },
      { name: "destination", label: "Destination", type: "address", required: true, span: 2 },
      PASSENGERS_FIELD,
      { name: "stops", label: "Number of Stops", type: "number", min: 0, max: 20, required: true },
      { name: "estimated_duration", label: "Estimated Duration (hours)", type: "number", min: 1, max: 24, required: true },
      { name: "recurring", label: "Recurring Service Required?", type: "yesno", required: true },
    ],
  },
  "Hourly Hire": {
    fields: [
      { name: "pickup_date", label: "Pickup Date", type: "date", required: true },
      { name: "pickup_time", label: "Pickup Time", type: "time", required: true },
      { name: "pickup_location", label: "Pickup Location", type: "address", required: true, span: 2 },
      { name: "hours_required", label: "Number of Hours Required", type: "number", min: 1, max: 24, required: true },
      PASSENGERS_FIELD,
      { name: "planned_destinations", label: "Planned Destinations (Optional)", type: "textarea", required: false, span: 2, placeholder: "List any places you'd like to visit..." },
      { name: "estimated_end_time", label: "Estimated End Time", type: "time", required: true },
    ],
  },
  "Cruise Transfers": {
    transferType: {
      name: "transfer_type",
      label: "Transfer Type",
      options: ["Arrival", "Departure", "Return (Both)"],
      required: true,
    },
    byTransfer: {
      Arrival: { fields: CRUISE_FIELDS },
      Departure: { fields: CRUISE_FIELDS },
      "Return (Both)": { fields: CRUISE_FIELDS },
    },
  },
  "Wedding Transfers": {
    fields: [
      { name: "couple_names", label: "Bride and Groom Name", type: "text", required: true, span: 2, placeholder: "e.g. Sophie & James" },
      { name: "wedding_venue", label: "Wedding Venue", type: "address", required: true, span: 2 },
      { name: "ceremony_date", label: "Ceremony Date", type: "date", required: true },
      { name: "ceremony_time", label: "Ceremony Time", type: "time", required: true },
      { name: "pickup_date", label: "Pickup Date", type: "date", required: true },
      { name: "pickup_time", label: "Pickup Time", type: "time", required: true },
      { name: "pickup_location", label: "Pickup Location", type: "address", required: true, span: 2 },
      PASSENGERS_FIELD,
      { name: "vehicles_required", label: "Number of Vehicles Required", type: "number", min: 1, max: 10, required: true },
      { name: "return_transfers", label: "Return Transfers Required?", type: "yesno", required: true, span: 2 },
    ],
  },
  "Special Events": {
    fields: [
      { name: "event_name", label: "Event Name", type: "text", required: true, span: 2 },
      { name: "event_location", label: "Event Location", type: "address", required: true, span: 2 },
      { name: "event_date", label: "Event Date", type: "date", required: true },
      { name: "event_start_time", label: "Event Start Time", type: "time", required: true },
      { name: "pickup_date", label: "Pickup Date", type: "date", required: true },
      { name: "pickup_time", label: "Pickup Time", type: "time", required: true },
      { name: "pickup_location", label: "Pickup Location", type: "address", required: true, span: 2 },
      PASSENGERS_FIELD,
      { name: "return_transfer", label: "Return Transfer Required?", type: "yesno", required: true },
    ],
  },
};
